import { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import {
  createLeadStep,
  getLeadSteps,
  reorderLeadSteps,
  updateLeadStep,
  deleteLeadStep,
  LeadStep,
} from "@/Api/lead-steps";
import { HexColorPicker } from "react-colorful";
import { toast } from "sonner";
import PageTemplate from "@/components/PageTemplate";
import { Card } from "@/components/ui/card";
import {
  GripVertical,
  Plus,
  Loader2,
  Trash2,
  RotateCcw,
  Pencil,
} from "lucide-react";
import { useTypeUser } from "@/hooks/auth";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

export function KanbanSettings() {
  const [columns, setColumns] = useState<LeadStep[]>([]);
  const [isNewColumnOpen, setIsNewColumnOpen] = useState(false);
  const [isEditColumnOpen, setIsEditColumnOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState<LeadStep | null>(null);
  const [newColumnName, setNewColumnName] = useState("");
  const [newColumnColor, setNewColumnColor] = useState("#ffffff");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const authOnlyClient = useTypeUser(2);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    fetchColumns();
  }, []);

  const fetchColumns = async () => {
    setIsLoading(true);
    try {
      const response = await getLeadSteps();
      if (response && response.status && Array.isArray(response.response)) {
        const sortedColumns = [...response.response].sort(
          (a, b) => a.order - b.order
        );
        setColumns(sortedColumns);
      } else {
        setColumns([]);
        toast.error("Erro ao carregar as colunas");
      }
    } catch (error: any) {
      setColumns([]);
      toast.error(error.message || "Erro ao carregar as colunas");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateColumn = async () => {
    if (!newColumnName.trim()) {
      toast.error("O nome da coluna é obrigatório");
      return;
    }

    setIsSaving(true);
    try {
      const response = await createLeadStep({
        name: newColumnName.trim(),
        color: newColumnColor,
      });

      if (response.status) {
        toast.success(response.message || "Coluna criada com sucesso");
        await fetchColumns();
        setIsNewColumnOpen(false);
        setNewColumnName("");
        setNewColumnColor("#ffffff");
      } else {
        toast.error(response.message || "Erro ao criar coluna");
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao criar coluna");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateColumn = async () => {
    if (!selectedColumn || !newColumnName.trim()) {
      toast.error("O nome da coluna é obrigatório");
      return;
    }

    setIsSaving(true);
    try {
      const response = await updateLeadStep(selectedColumn.uuid, {
        name: newColumnName.trim(),
        color: newColumnColor,
      });

      if (response.status) {
        toast.success(response.message || "Coluna atualizada com sucesso");
        await fetchColumns();
        setIsEditColumnOpen(false);
        setSelectedColumn(null);
        setNewColumnName("");
        setNewColumnColor("#ffffff");
      } else {
        toast.error(response.message || "Erro ao atualizar coluna");
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao atualizar coluna");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteColumn = async (uuid: string) => {
    setIsLoading(true);
    try {
      const response = await deleteLeadStep(uuid);
      if (response.status) {
        toast.success(response.message || "Coluna excluída com sucesso");
        await fetchColumns();
      } else {
        toast.error(response.message || "Erro ao excluir coluna");
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao excluir coluna");
    } finally {
      setIsLoading(false);
      setIsDeleteAlertOpen(false);
      setSelectedColumn(null);
    }
  };

  const handleResetOrder = async () => {
    if (!confirm("Tem certeza que deseja resetar a ordem das colunas?")) return;

    setIsLoading(true);
    try {
      const reorderData = columns.map((item, index) => ({
        id: item.id,
        order: index + 1,
      }));

      const response = await reorderLeadSteps(reorderData);
      if (response.status) {
        setColumns(response.response);
        toast.success("Ordem resetada com sucesso");
      } else {
        toast.error(response.message || "Erro ao resetar ordem");
        await fetchColumns();
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao resetar ordem");
      await fetchColumns();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(columns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const reorderData = items.map((item, index) => ({
      id: item.id,
      order: index + 1,
    }));

    setIsLoading(true);
    try {
      const response = await reorderLeadSteps(reorderData);
      if (response.status) {
        setColumns(response.response);
        toast.success("Ordem atualizada com sucesso");
      } else {
        toast.error(response.message || "Erro ao reordenar colunas");
        await fetchColumns();
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao reordenar colunas");
      await fetchColumns();
    } finally {
      setIsLoading(false);
    }
  };

  const openEditColumn = (column: LeadStep) => {
    setSelectedColumn(column);
    setNewColumnName(column.name);
    setNewColumnColor(column.color);
    setIsEditColumnOpen(true);
  };

  const openDeleteAlert = (column: LeadStep) => {
    setSelectedColumn(column);
    setIsDeleteAlertOpen(true);
  };

  if (!authOnlyClient) return null;

  return (
    <PageTemplate>
      <If condition={hasPermission("view-configuration-kanban-list")}>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Configurações do Kanban</h1>
            <p className="text-sm text-gray-500 mt-1">
              Arraste as colunas para reordenar. Total: {columns.length} colunas
            </p>
          </div>
          <div className="flex gap-2">
            <If condition={hasPermission("reset-orders-kanban")}>
              <Button
                variant="outline"
                onClick={handleResetOrder}
                disabled={isLoading || columns.length === 0}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Resetar Ordem
              </Button>
            </If>
            <If condition={hasPermission("create-column-kanban")}>
              <Button
                onClick={() => setIsNewColumnOpen(true)}
                disabled={isLoading}
              >
                <Plus className="mr-2 h-4 w-4" /> Nova Coluna
              </Button>
            </If>
          </div>
        </div>

        <Card className="p-6">
          {isLoading ? (
            <div className="flex justify-center items-center p-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : columns.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhuma coluna cadastrada
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="columns">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="space-y-2"
                  >
                    {columns.map((column, index) => (
                      <Draggable
                        key={column.id}
                        draggableId={String(column.id)}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className="flex items-center justify-between p-4 bg-white rounded-lg border hover:border-blue-500 transition-colors"
                          >
                            <div className="flex items-center gap-4">
                              <div {...provided.dragHandleProps}>
                                <GripVertical className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                              </div>
                              <div
                                className="w-4 h-4 rounded-full border border-gray-200"
                                style={{ backgroundColor: column.color }}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {column.name}
                                </span>
                                <span className="text-sm text-gray-500">
                                  Ordem: {column.order}
                                </span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <If
                                condition={hasPermission("edit-column-kanban")}
                              >
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditColumn(column)}
                                  disabled={isLoading}
                                >
                                  <Pencil className="h-4 w-4 mr-1" />
                                  Editar
                                </Button>
                              </If>
                              <If
                                condition={hasPermission(
                                  "delete-column-kanban"
                                )}
                              >
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openDeleteAlert(column)}
                                  disabled={
                                    isLoading ||
                                    column.id === 2 ||
                                    column.id === 1
                                  }
                                  className="bg-red-500 hover:bg-red-600 text-white"
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Excluir
                                </Button>
                              </If>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Card>

        <Dialog open={isNewColumnOpen} onOpenChange={setIsNewColumnOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Nova Coluna</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  placeholder="Nome da coluna"
                  disabled={isSaving}
                />
              </div>
              <div className="grid gap-2">
                <Label>Cor</Label>
                <div className="flex justify-center p-4 border rounded-lg">
                  <HexColorPicker
                    color={newColumnColor}
                    onChange={setNewColumnColor}
                  />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <div
                    className="w-8 h-8 rounded border"
                    style={{ backgroundColor: newColumnColor }}
                  />
                  <Input
                    value={newColumnColor}
                    onChange={(e) => setNewColumnColor(e.target.value)}
                    placeholder="#000000"
                    disabled={isSaving}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsNewColumnOpen(false)}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button onClick={handleCreateColumn} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Criando...
                  </>
                ) : (
                  "Criar"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isEditColumnOpen} onOpenChange={setIsEditColumnOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Editar Coluna</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  placeholder="Nome da coluna"
                  disabled={isSaving}
                />
              </div>
              <div className="grid gap-2">
                <Label>Cor</Label>
                <div className="flex justify-center p-4 border rounded-lg">
                  <HexColorPicker
                    color={newColumnColor}
                    onChange={setNewColumnColor}
                  />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <div
                    className="w-8 h-8 rounded border"
                    style={{ backgroundColor: newColumnColor }}
                  />
                  <Input
                    value={newColumnColor}
                    onChange={(e) => setNewColumnColor(e.target.value)}
                    placeholder="#000000"
                    disabled={isSaving}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsEditColumnOpen(false)}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button onClick={handleUpdateColumn} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog
          open={isDeleteAlertOpen}
          onOpenChange={setIsDeleteAlertOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. Isso excluirá permanentemente a
                coluna{" "}
                <span className="font-semibold">{selectedColumn?.name}</span> e
                todos os dados associados a ela.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setIsDeleteAlertOpen(false);
                  setSelectedColumn(null);
                }}
              >
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-500 hover:bg-red-600 text-white"
                onClick={() =>
                  selectedColumn && handleDeleteColumn(selectedColumn.uuid)
                }
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </If>
    </PageTemplate>
  );
}
